import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  get,
  getAll,
  create,
  deleteWaterfall,
  update,
  createVersion,
  updateVersion,
  deleteVersion,
} from '../services/waterfallService';
import {
  CreateOrUpdateWaterfallVersionViewModel,
  CreateOrUpdateWaterfallViewModel,
  WaterfallViewModel,
} from '../models/waterfall';

export const useWaterfalls = (id?: number) => {
  const queryClient = useQueryClient();

  const waterfallsQuery = useQuery<WaterfallViewModel[]>({
    queryKey: ['waterfalls'],
    queryFn: getAll,
    staleTime: 30_000,
  });

  const waterfallDetailQuery = useQuery<WaterfallViewModel>({
    queryKey: ['waterfall', id],
    queryFn: () => get(id!),
    enabled: !!id,
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateWaterfallViewModel) => create(payload),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.setQueryData(['waterfall', created.waterfallId], created);
    },
  });

  const updateWaterfallMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateWaterfallViewModel) => update(payload),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.setQueryData(['waterfall', updated.waterfallId], updated);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteWaterfall(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.removeQueries({ queryKey: ['waterfall', id] });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateWaterfallVersionViewModel) => createVersion(payload),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ['waterfall', payload.waterfallId] });
    },
  });

  const updateVersionMutation = useMutation({
    mutationFn: (payload: CreateOrUpdateWaterfallVersionViewModel) => updateVersion(payload),
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ['waterfall', payload.waterfallId] });
    },
  });

  const deleteVersionMutation = useMutation({
    mutationFn: (id: number) => deleteVersion(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['waterfalls'] });
      queryClient.invalidateQueries({ queryKey: ['waterfall', id] });
    },
  });

  return {
    waterfallsQuery,
    waterfallDetailQuery,
    createWaterfall: createMutation.mutateAsync,
    updateWaterfall: updateWaterfallMutation.mutateAsync,
    deleteWaterfall: deleteMutation.mutateAsync,
    createVersion: createVersionMutation.mutateAsync,
    updateVersion: updateVersionMutation.mutateAsync,
    deleteVersion: deleteVersionMutation.mutateAsync,
  };
};
